import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

// Simple custom page renderer placeholder; can be enhanced to render blocks
export default function CustomPage(){
  const { pageSlug } = useParams();
  const { themeStyles } = useOutletContext() || { themeStyles: {} };
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const resp = await apiFetch(`/pages/slug/${pageSlug}`);
        if (!resp.ok) {
          throw new Error('Page not found');
        }
        const data = await resp.json();
        setPage(data.page);
      } catch (e) {
        setError(e.message || 'Failed to load page');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [pageSlug]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading page...</div>;
  if (error || !page) return <div style={{ padding: '2rem', textAlign: 'center' }}>{error || 'Page not found'}</div>;

  return (
    <>
      <style>{`
        /* Mobile Optimizations for CustomPage */
        @media (max-width: 768px) {
          .custom-page-container {
            padding: 1.5rem 0 !important;
          }
          .custom-page-inner {
            padding: 0 1rem !important;
          }
          .custom-page-title {
            font-size: 1.5rem !important;
          }
          .custom-page-description {
            font-size: 0.9rem !important;
          }
          .custom-hero-section {
            padding: 2rem 1rem !important;
          }
          .custom-hero-title {
            font-size: 1.75rem !important;
          }
          .custom-columns-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
          .custom-block {
            padding: 1rem !important;
          }
        }

        @media (max-width: 480px) {
          .custom-page-container {
            padding: 1rem 0 !important;
          }
          .custom-page-inner {
            padding: 0 0.75rem !important;
          }
          .custom-page-title {
            font-size: 1.25rem !important;
          }
          .custom-hero-section {
            padding: 1.5rem 0.75rem !important;
          }
          .custom-hero-title {
            font-size: 1.5rem !important;
          }
        }
      `}</style>
      <div className="custom-page-container" style={{ padding: '2rem 0' }}>
        <div className="custom-page-inner" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <h1 className="custom-page-title" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem', color: themeStyles?.text || '#111827' }}>{page.title}</h1>
          {page.description && (
            <p className="custom-page-description" style={{ color: themeStyles?.textLight || '#6b7280', marginBottom: '1.5rem' }}>{page.description}</p>
          )}
        {/* Simple block renderer */}
        {Array.isArray(page.blocks) && page.blocks.length > 0 ? (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            {page.blocks.map((block, idx) => {
              const type = block.type || block.id?.split('-')[0];
              const data = block.data || block;
              if (type === 'hero') {
                return (
                  <section key={idx} className="custom-hero-section" style={{
                    background: `linear-gradient(135deg, ${themeStyles.primary} 0%, ${themeStyles.secondary} 100%)`,
                    color: '#fff', padding: '3rem 1rem', borderRadius: 12
                  }}>
                    <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
                      <h2 className="custom-hero-title" style={{ fontSize: '2.25rem', margin: 0 }}>{data.title || 'Hero Title'}</h2>
                      {data.subtitle && <p style={{ opacity: 0.9, marginTop: 12 }}>{data.subtitle}</p>}
                    </div>
                  </section>
                );
              }
              if (type === 'products') {
                return (
                  <section key={idx} className="custom-block">
                    <h3 style={{ fontSize: '1.5rem', margin: '0 0 0.75rem 0' }}>{data.title || 'Products'}</h3>
                    <p style={{ color: themeStyles.textLight, margin: '0 0 1rem 0' }}>{data.description || ''}</p>
                    <div style={{ padding: '1rem', border: '1px dashed #e5e7eb', borderRadius: 8 }}>
                      Products block placeholder (renders store catalog on native pages).
                    </div>
                  </section>
                );
              }
              if (type === 'columns') {
                const count = Math.max(1, Math.min(3, parseInt(data.columnCount || 2, 10)));
                return (
                  <section key={idx} className="custom-block">
                    {data.title && <h3 style={{ fontSize: '1.25rem', margin: '0 0 0.75rem 0' }}>{data.title}</h3>}
                    <div className="custom-columns-grid" style={{ display: 'grid', gap: 16, gridTemplateColumns: `repeat(${count}, minmax(0,1fr))` }}>
                      {(new Array(count)).fill(0).map((_, i) => (
                        <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16 }}>
                          {(i===0 ? data.column1Content : i===1 ? data.column2Content : data.column3Content) || 'Content'}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              }
              if (type === 'forms') {
                return (
                  <section key={idx} className="custom-block">
                    <h3 style={{ fontSize: 'var(--font-size-xl)', margin: '0 0 var(--space-3) 0' }}>{data.title || 'Contact'}</h3>
                    <form style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 560 }} onSubmit={(e)=>e.preventDefault()}>
                      <input placeholder="Name" style={{ padding: 'var(--space-3)', border: '1px solid #e5e7eb', borderRadius: 'var(--radius-md)', minHeight: '44px' }} />
                      <input placeholder="Email" style={{ padding: 'var(--space-3)', border: '1px solid #e5e7eb', borderRadius: 'var(--radius-md)', minHeight: '44px' }} />
                      <textarea placeholder="Message" rows={4} style={{ padding: 'var(--space-3)', border: '1px solid #e5e7eb', borderRadius: 'var(--radius-md)' }} />
                      <button style={{ padding: 'var(--space-2) var(--space-4)', background: themeStyles.primary, color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', minHeight: '44px' }}>Send</button>
                    </form>
                  </section>
                );
              }
              if (type === 'reviews') {
                return (
                  <section key={idx} className="custom-block">
                    <h3 style={{ fontSize: 'var(--font-size-xl)', margin: '0 0 var(--space-3) 0' }}>{data.title || 'Reviews'}</h3>
                    <div style={{ padding: 'var(--space-4)', border: '1px dashed #e5e7eb', borderRadius: 'var(--radius-md)' }}>Reviews block placeholder.</div>
                  </section>
                );
              }
              return (
                <section key={idx} className="custom-block">
                  <div style={{ padding: 'var(--space-4)', border: '1px dashed #e5e7eb', borderRadius: 'var(--radius-md)' }}>Unsupported block: {type}</div>
                </section>
              );
            })}
          </div>
        ) : (
          <div>No content added yet.</div>
        )}
      </div>
    </div>
    </>
  );
}


